import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { PitchArrivalTypeSettingsEditor } from "@/components/pitches/PitchArrivalTypeSettingsEditor";
import { PitchFeatureSelect } from "@/components/pitches/PitchFeatureSelect";
import { api } from "@/lib/apiClient";
import { usePitchFeatures } from "@/lib/usePitchFeatures";
import { isDevDataDeletionHost } from "@/lib/platform";
import {
  PITCH_USAGE_TYPE_OPTIONS,
  arrivalSettingsToApi,
  emptyArrivalTypeSettings,
  type PitchArrivalTypeSettingsForm,
  type PitchUsageType,
} from "@/lib/pitchUsageType";
import type { ArrivalTypeRecord, Pitch, PitchCategory } from "@/types/camping";

interface BulkOperationResult {
  message: string;
  errors: { pitch_id: string | null; message: string }[];
}

interface BulkEditResult extends BulkOperationResult {
  updated: number;
  skipped: number;
}

interface BulkDeactivateResult extends BulkOperationResult {
  deactivated: number;
  skipped: number;
}

interface BulkHardDeleteResult extends BulkOperationResult {
  deleted: number;
  skipped: number;
}

type TriState = "unchanged" | "yes" | "no";

interface FieldToggle {
  enabled: boolean;
  value: string | TriState;
}

interface ArrayFieldToggle {
  enabled: boolean;
  value: string[];
}

interface BulkEditForm {
  category_id: FieldToggle;
  usage_type: FieldToggle;
  size_sqm: FieldToggle;
  arrival_type_settings: FieldToggle;
  restrict_arrival_types: boolean;
  arrival_type_settings_value: PitchArrivalTypeSettingsForm;
  has_electricity: FieldToggle;
  has_water: FieldToggle;
  has_sewage: FieldToggle;
  is_blocked: FieldToggle;
  is_active: FieldToggle;
  feature_ids: ArrayFieldToggle;
  notes: FieldToggle;
  fernauslese_channel: FieldToggle;
}

function emptyField(value: string | TriState = "unchanged"): FieldToggle {
  return { enabled: false, value };
}

function emptyBulkForm(arrivalTypes: ArrivalTypeRecord[]): BulkEditForm {
  return {
    category_id: emptyField(""),
    usage_type: emptyField("tourism"),
    size_sqm: emptyField(""),
    arrival_type_settings: emptyField(),
    restrict_arrival_types: false,
    arrival_type_settings_value: emptyArrivalTypeSettings(arrivalTypes),
    has_electricity: emptyField(),
    has_water: emptyField(),
    has_sewage: emptyField(),
    is_blocked: emptyField(),
    is_active: emptyField(),
    feature_ids: { enabled: false, value: [] },
    notes: emptyField(""),
    fernauslese_channel: emptyField(""),
  };
}

export interface BulkEditPitchModalProps {
  open: boolean;
  pitches: Pitch[];
  categories: PitchCategory[];
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
  onDeleted?: () => void;
}

function triStateCheckbox(
  field: FieldToggle,
  onChange: (next: FieldToggle) => void,
  label: string,
) {
  return (
    <div className="space-y-2 rounded-lg border border-separator p-3">
      <label className="flex items-center gap-2 text-sm font-medium text-label">
        <input
          type="checkbox"
          className="rounded border-separator text-accent focus:ring-accent"
          checked={field.enabled}
          onChange={(e) => onChange({ ...field, enabled: e.target.checked })}
        />
        {label}
      </label>
      {field.enabled ? (
        <div className="flex gap-4 pl-6 text-sm">
          {(["yes", "no"] as const).map((state) => (
            <label key={state} className="flex items-center gap-2">
              <input
                type="radio"
                name={`bulk-${label}`}
                checked={field.value === state}
                onChange={() => onChange({ ...field, value: state })}
              />
              {state === "yes" ? "Ja" : "Nein"}
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function BulkEditPitchModal({
  open,
  pitches,
  categories,
  canEdit,
  onClose,
  onSaved,
  onDeleted,
}: BulkEditPitchModalProps) {
  const isDev = isDevDataDeletionHost();
  const { features } = usePitchFeatures(open);
  const [arrivalTypes, setArrivalTypes] = useState<ArrivalTypeRecord[]>([]);
  const [form, setForm] = useState<BulkEditForm>(() => emptyBulkForm([]));
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkEditResult | null>(null);
  const activeCount = pitches.filter((p) => p.is_active).length;

  useEffect(() => {
    if (!open) return;
    api
      .get<ArrivalTypeRecord[]>("/api/v1/arrival-types")
      .then(setArrivalTypes)
      .catch(() => setArrivalTypes([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setForm(emptyBulkForm(arrivalTypes));
    setError(null);
    setResult(null);
    setSubmitting(false);
    setDeleting(false);
  }, [open, arrivalTypes]);

  const pitchIds = pitches.map((p) => p.id);
  const codesPreview =
    pitches.length <= 6
      ? pitches.map((p) => p.code).join(", ")
      : `${pitches
          .slice(0, 5)
          .map((p) => p.code)
          .join(", ")} … (+${pitches.length - 5})`;

  async function handleSoftDelete() {
    if (!canEdit || activeCount === 0) return;
    const confirmed = window.confirm(
      `${activeCount} Stellplatz${activeCount === 1 ? "" : "e"} ausblenden?\n\nDie Plätze bleiben in der Datenbank erhalten, erscheinen aber nicht mehr in der Platzliste und sind nicht mehr buchbar. Bestehende Buchungen bleiben unverändert.`,
    );
    if (!confirmed) return;
    setError(null);
    setResult(null);
    setDeleting(true);
    try {
      const res = await api.post<BulkDeactivateResult>("/api/v1/pitches/bulk/deactivate", {
        pitch_ids: pitchIds,
      });
      if (res.deactivated > 0) {
        onDeleted?.();
        onSaved();
      }
      if (res.errors.length > 0) {
        setError(res.errors.map((e) => e.message).join("\n"));
      }
      if (res.deactivated > 0 && res.errors.length === 0) {
        onClose();
      } else if (res.deactivated === 0) {
        setError((prev) => prev ?? res.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen");
    } finally {
      setDeleting(false);
    }
  }

  async function handleHardDelete() {
    if (!canEdit || !isDev) return;
    const first = window.confirm(
      `${pitches.length} Stellplatz${pitches.length === 1 ? "" : "e"} ENDGÜLTIG aus der Datenbank löschen?\n\nNur in der Dev-Umgebung. Dies kann nicht rückgängig gemacht werden.`,
    );
    if (!first) return;
    const second = window.confirm(
      `Wirklich löschen? Betroffene Codes: ${codesPreview}`,
    );
    if (!second) return;
    setError(null);
    setResult(null);
    setDeleting(true);
    try {
      const res = await api.post<BulkHardDeleteResult>("/api/v1/dev/pitches/bulk-delete", {
        pitch_ids: pitchIds,
      });
      if (res.deleted > 0) {
        onDeleted?.();
        onSaved();
      }
      if (res.errors.length > 0) {
        setError(res.errors.map((e) => e.message).join("\n"));
      }
      if (res.deleted > 0 && res.errors.length === 0) {
        onClose();
      } else if (res.deleted === 0) {
        setError((prev) => prev ?? res.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Endgültiges Löschen fehlgeschlagen");
    } finally {
      setDeleting(false);
    }
  }

  function buildUpdates(): Record<string, unknown> {
    const updates: Record<string, unknown> = {};
    if (form.category_id.enabled) {
      updates.category_id = (form.category_id.value as string) || null;
    }
    if (form.usage_type.enabled && form.usage_type.value) {
      updates.usage_type = form.usage_type.value as PitchUsageType;
    }
    if (form.size_sqm.enabled && form.size_sqm.value) {
      updates.size_sqm = Number(form.size_sqm.value);
    }
    if (form.arrival_type_settings.enabled) {
      updates.arrival_type_settings = arrivalSettingsToApi(
        form.arrival_type_settings_value,
        form.restrict_arrival_types,
      );
    }
    for (const key of [
      "has_electricity",
      "has_water",
      "has_sewage",
      "is_blocked",
      "is_active",
    ] as const) {
      const field = form[key];
      if (field.enabled && field.value !== "unchanged") {
        updates[key] = field.value === "yes";
      }
    }
    if (form.feature_ids.enabled) {
      updates.feature_ids = form.feature_ids.value;
    }
    if (form.notes.enabled) {
      updates.notes = String(form.notes.value).trim() || null;
    }
    if (form.fernauslese_channel.enabled) {
      const raw = String(form.fernauslese_channel.value).trim();
      updates.fernauslese_channel = raw ? Number(raw) : null;
    }
    return updates;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const updates = buildUpdates();
    if (Object.keys(updates).length === 0) {
      setError("Bitte mindestens ein Feld zum Ändern auswählen.");
      return;
    }
    setError(null);
    setResult(null);
    setSubmitting(true);
    try {
      const res = await api.patch<BulkEditResult>("/api/v1/pitches/bulk", {
        pitch_ids: pitches.map((p) => p.id),
        updates,
      });
      setResult(res);
      if (res.updated > 0) onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Stellplätze bearbeiten"
      description={`Gemeinsame Eigenschaften für ${pitches.length} Plätze ändern`}
      size="xl"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <p className="text-sm text-label-secondary">
          Ausgewählt: <span className="font-medium text-label">{codesPreview}</span>
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-2 rounded-lg border border-separator p-3 text-sm sm:col-span-2">
            <span className="flex items-center gap-2 font-medium text-label">
              <input
                type="checkbox"
                className="rounded border-separator text-accent focus:ring-accent"
                checked={form.category_id.enabled}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    category_id: { ...f.category_id, enabled: e.target.checked },
                  }))
                }
              />
              Kategorie ändern
            </span>
            {form.category_id.enabled ? (
              <select
                className="eh-select-input ml-6"
                value={form.category_id.value as string}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    category_id: { ...f.category_id, value: e.target.value },
                  }))
                }
              >
                <option value="">Keine Kategorie</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            ) : null}
          </label>

          <label className="space-y-2 rounded-lg border border-separator p-3 text-sm sm:col-span-2">
            <span className="flex items-center gap-2 font-medium text-label">
              <input
                type="checkbox"
                className="rounded border-separator text-accent focus:ring-accent"
                checked={form.usage_type.enabled}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    usage_type: { ...f.usage_type, enabled: e.target.checked },
                  }))
                }
              />
              Platztyp ändern
            </span>
            {form.usage_type.enabled ? (
              <select
                className="eh-select-input ml-6"
                value={form.usage_type.value as string}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    usage_type: { ...f.usage_type, value: e.target.value },
                  }))
                }
              >
                {PITCH_USAGE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : null}
          </label>

          <label className="space-y-2 rounded-lg border border-separator p-3 text-sm">
            <span className="flex items-center gap-2 font-medium text-label">
              <input
                type="checkbox"
                className="rounded border-separator text-accent focus:ring-accent"
                checked={form.size_sqm.enabled}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    size_sqm: { ...f.size_sqm, enabled: e.target.checked },
                  }))
                }
              />
              Größe m²
            </span>
            {form.size_sqm.enabled ? (
              <input
                className="eh-input ml-6"
                value={form.size_sqm.value as string}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    size_sqm: { ...f.size_sqm, value: e.target.value },
                  }))
                }
              />
            ) : null}
          </label>
        </div>

        {arrivalTypes.length > 0 ? (
          <div className="space-y-2 rounded-lg border border-separator p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-label">
              <input
                type="checkbox"
                className="rounded border-separator text-accent focus:ring-accent"
                checked={form.arrival_type_settings.enabled}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    arrival_type_settings: {
                      ...f.arrival_type_settings,
                      enabled: e.target.checked,
                    },
                  }))
                }
              />
              Anreisearten-Einschränkung setzen
            </label>
            {form.arrival_type_settings.enabled ? (
              <div className="ml-6">
                <PitchArrivalTypeSettingsEditor
                  arrivalTypes={arrivalTypes}
                  restrictArrivalTypes={form.restrict_arrival_types}
                  onRestrictChange={(restrict_arrival_types) =>
                    setForm((f) => ({
                      ...f,
                      restrict_arrival_types,
                      arrival_type_settings_value: restrict_arrival_types
                        ? f.arrival_type_settings_value
                        : emptyArrivalTypeSettings(arrivalTypes),
                    }))
                  }
                  value={form.arrival_type_settings_value}
                  onChange={(arrival_type_settings_value) =>
                    setForm((f) => ({ ...f, arrival_type_settings_value }))
                  }
                />
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          {triStateCheckbox(form.has_electricity, (next) =>
            setForm((f) => ({ ...f, has_electricity: next })),
          "Strom")}
          {triStateCheckbox(form.has_water, (next) =>
            setForm((f) => ({ ...f, has_water: next })),
          "Wasser")}
          {triStateCheckbox(form.has_sewage, (next) =>
            setForm((f) => ({ ...f, has_sewage: next })),
          "Abwasser")}
          {triStateCheckbox(form.is_blocked, (next) =>
            setForm((f) => ({ ...f, is_blocked: next })),
          "Gesperrt")}
          {triStateCheckbox(form.is_active, (next) =>
            setForm((f) => ({ ...f, is_active: next })),
          "Aktiv")}
        </div>

        <div className="space-y-2 rounded-lg border border-separator p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-label">
            <input
              type="checkbox"
              className="rounded border-separator text-accent focus:ring-accent"
              checked={form.feature_ids.enabled}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  feature_ids: { ...f.feature_ids, enabled: e.target.checked },
                }))
              }
            />
            Eigenschaften setzen
          </label>
          {form.feature_ids.enabled ? (
            <div className="ml-6">
              <PitchFeatureSelect
                features={features}
                selectedIds={form.feature_ids.value}
                onChange={(value) =>
                  setForm((f) => ({
                    ...f,
                    feature_ids: { ...f.feature_ids, value },
                  }))
                }
                emptyHint="Noch keine Eigenschaften angelegt – über „Eigenschaften verwalten“ in der Platzliste anlegen."
              />
            </div>
          ) : null}
        </div>

        <label className="block space-y-2 rounded-lg border border-separator p-3 text-sm">
          <span className="flex items-center gap-2 font-medium text-label">
            <input
              type="checkbox"
              className="rounded border-separator text-accent focus:ring-accent"
              checked={form.notes.enabled}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  notes: { ...f.notes, enabled: e.target.checked },
                }))
              }
            />
            Notizen setzen
          </span>
          {form.notes.enabled ? (
            <textarea
              className="eh-input ml-6 min-h-[4rem] resize-y"
              value={form.notes.value as string}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  notes: { ...f.notes, value: e.target.value },
                }))
              }
              rows={2}
            />
          ) : null}
        </label>

        <label className="block max-w-xs space-y-2 rounded-lg border border-separator p-3 text-sm">
          <span className="flex items-center gap-2 font-medium text-label">
            <input
              type="checkbox"
              className="rounded border-separator text-accent focus:ring-accent"
              checked={form.fernauslese_channel.enabled}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  fernauslese_channel: { ...f.fernauslese_channel, enabled: e.target.checked },
                }))
              }
            />
            Fernauslese Kanal-ID
          </span>
          {form.fernauslese_channel.enabled ? (
            <input
              type="number"
              min={1}
              className="eh-input ml-6"
              value={form.fernauslese_channel.value as string}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  fernauslese_channel: { ...f.fernauslese_channel, value: e.target.value },
                }))
              }
              placeholder="Leer = entfernen"
            />
          ) : null}
        </label>

        {result ? (
          <div className="rounded-lg border border-separator bg-fill-tertiary/40 p-3 text-sm">
            <p className="font-medium text-label">{result.message}</p>
            {result.errors.length > 0 ? (
              <ul className="mt-2 max-h-32 overflow-y-auto text-status-red">
                {result.errors.map((err, i) => (
                  <li key={`${err.pitch_id ?? i}-${err.message}`}>{err.message}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap items-center justify-between gap-2 border-t border-separator pt-4">
          {canEdit ? (
            <div className="flex flex-wrap gap-2">
              {activeCount > 0 ? (
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => void handleSoftDelete()}
                  loading={deleting}
                  disabled={submitting || deleting}
                >
                  {activeCount} Plätze ausblenden
                </Button>
              ) : null}
              {isDev ? (
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => void handleHardDelete()}
                  loading={deleting}
                  disabled={submitting || deleting}
                >
                  Endgültig löschen (Dev)
                </Button>
              ) : null}
            </div>
          ) : (
            <span />
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={submitting || deleting}
            >
              {result?.updated ? "Schließen" : "Abbrechen"}
            </Button>
            {!result?.updated ? (
              <Button type="submit" loading={submitting} disabled={deleting}>
                {pitches.length} Plätze aktualisieren
              </Button>
            ) : null}
          </div>
        </div>
      </form>
    </Modal>
  );
}
